import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { MessagePayload, onMessage } from 'firebase/messaging';
import { notifications } from '@mantine/notifications';
import { firebaseMessaging } from '@/src/lib/firebase/config';
import { SChatNotificationPayload } from '@/src/modules/notification/data/payloads';
import { TNotificationType } from '@/src/modules/notification/enums/notification-type';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import { EChatTab } from '../chat/_enums/chat-tab';
import { EChatTabTarget } from '../chat/_enums/chat-tab-target';

interface IShowNotificationParams {
  title: string;
  message: string;
  redirectUrl?: string | null;
}

export function useForegroundNotification() {
  const router = useRouter();

  const handleNotificationClick = ({
    redirectUrl,
  }: Pick<IShowNotificationParams, 'redirectUrl'>) => {
    if (!redirectUrl) {
      return;
    }
    router.push(redirectUrl);
  };

  const showNotification = ({
    title,
    message,
    redirectUrl = null,
  }: IShowNotificationParams) => {
    notifications.show({
      title,
      message,
      onClick: () => handleNotificationClick({ redirectUrl }),
    });
  };

  const getNotificationRedirectUrl = (data: any) => {
    const notificationType = data?.type?.toLowerCase() as TNotificationType;
    if (!notificationType) {
      return '/';
    }

    let params = {};

    if (notificationType.endsWith('_chat')) {
      switch (notificationType) {
        case 'marketplace_chat': {
          const parsedData = safeParseZodObject(SChatNotificationPayload, data);
          params = {
            tab: EChatTab.WithSeller,
            branch_id: parsedData.branch_id,
          };
          break;
        }
        case 'self_medication_chat':
          params = {
            tab: EChatTab.Support,
            target: EChatTabTarget.SelfMedication,
          };
          break;
        case 'complaint_chat':
          params = { tab: EChatTab.Support, target: EChatTabTarget.Complaint };
          break;
      }

      const searchParams = new URLSearchParams(params);
      return `/chat?${searchParams.toString()}`;
    }

    return '/';
  };

  const handleIncomingMessage = (payload: MessagePayload) => {
    const redirectUrl = getNotificationRedirectUrl(payload.data);
    const title = payload.notification?.title ?? 'Notifikasi';
    const message =
      payload.notification?.body ?? 'Anda menerima notifikasi baru.';
    showNotification({ title, message, redirectUrl });
  };

  useEffect(() => {
    if (!firebaseMessaging) {
      return;
    }

    const unsubscribe = onMessage(firebaseMessaging, (payload) => {
      handleIncomingMessage(payload);
    });
    return () => unsubscribe();
  }, []);
}
